import { dismissOpenOverlays } from "./dismissOpenOverlays";

/**
 * Close any open Select/Dropdown/DatePicker/Cascader/Popconfirm dropdown the
 * instant a new antd Modal or Drawer opens, so the two never render on top
 * of each other - e.g. a Dropdown left open near a button that also opens a
 * Modal stays open, floating over the Modal's content, because opening the
 * Modal is not a "click outside the dropdown" event as far as the dropdown
 * is concerned.
 *
 * Modal/Drawer are portalled to `document.body` (`.ant-modal-wrap` /
 * `.ant-drawer`) only once opened - there is no single call site to hook for
 * "a modal opened" across the hundreds of components in this app that each
 * manage their own `visible`/`open` boolean, so this watches for that DOM
 * node appearing instead. See dismissOpenOverlays for how the actual dismiss
 * works and why it's safe for the rest of the app (in particular, it does
 * NOT close the Modal/Drawer that just opened - antd's Modal/Drawer manage
 * their own open state explicitly via props, not via rc-trigger's
 * document-mousedown auto-dismiss, which only click-triggered dropdowns use).
 */
const OVERLAY_ROOT_SELECTOR = ".ant-modal-wrap, .ant-drawer";

const hasOverlayRoot = (node) => {
  if (node.nodeType !== 1) return false; // Element nodes only
  return node.matches?.(OVERLAY_ROOT_SELECTOR) || !!node.querySelector?.(OVERLAY_ROOT_SELECTOR);
};

const handleMutations = (mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (hasOverlayRoot(node)) {
        dismissOpenOverlays();
        return;
      }
    }
  }
};

/**
 * Start watching. @returns {Function} unsubscribe
 */
const closeOverlaysOnModalOpen = () => {
  const observer = new MutationObserver(handleMutations);
  observer.observe(document.body, { childList: true, subtree: true });
  return () => observer.disconnect();
};

export default closeOverlaysOnModalOpen;
